// src/components/MapComponent.jsx
import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import MetroMinutoPopup from "./MetroMinutoPopup";

// Estilos agrupados
const containerStyle = {
  backgroundColor: '#FFD437',
  padding: '24px',
  borderRadius: '12px',
  maxWidth: '900px',
  width: '100%',
  margin: '30px auto',
  fontFamily: 'Arial, sans-serif',
  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
  overflowY: 'auto',
  maxHeight: '90vh',
};

const titleStyle = {
  color: '#305E67',
  borderBottom: '2px solid #09BAC4',
  paddingBottom: '12px',
  marginBottom: '24px',
  textAlign: 'center',
  fontSize: '24px',
  fontWeight: '600',
  letterSpacing: '1px',
};

const mapStyle = {
  width: '100%',
  height: '400px',
  borderRadius: '12px',
  marginBottom: '20px',
  border: '2px solid #ccc',
};

const buttonBase = {
  padding: '14px 24px',
  borderRadius: '8px',
  border: 'none',
  fontSize: '16px',
  fontWeight: 'bold',
  cursor: 'pointer',
  transition: 'background 0.3s ease',
};

const startButtonStyle = {
  ...buttonBase,
  backgroundColor: '#28a745',
  color: 'white',
  marginRight: '8px',
};

const stopButtonStyle = {
  ...buttonBase,
  backgroundColor: '#dc3545',
  color: 'white',
};

const backButtonStyle = {
  ...buttonBase,
  backgroundColor: '#305E67',
  color: 'white',
  display: 'block',
  margin: '16px auto 0',
};

const overlayStyle = {
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  backgroundColor: 'rgba(255,255,255,0.8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  fontSize: '18px',
  fontWeight: '600',
};

const MapComponent = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const markerRef = useRef(null);
  const polylineRef = useRef(null);
  const watchIdRef = useRef(null);
  const [showPopup, setShowPopup] = useState(true);
  const [clearRoute, setClearRoute] = useState(false);
  const [tripData, setTripData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [tripStarted, setTripStarted] = useState(false); // 👈 Nuevo estado

  // Inicialización del mapa
  useEffect(() => {
    if (!mapRef.current) {
      mapRef.current = L.map("map").setView([20.967, -89.623], 15);
      L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png").addTo(mapRef.current);

      markerRef.current = L.marker([20.967, -89.623], {
        icon: L.icon({
          iconUrl: "./img/autobus.png",
          iconSize: [40, 40],
          iconAnchor: [20, 40],
          popupAnchor: [0, -40],
        }),
      }).addTo(mapRef.current);

      polylineRef.current = L.polyline([], { color: "#05B1BD" }).addTo(mapRef.current);
    }
  }, []);

  // Limpia la ruta si se solicita
  useEffect(() => {
    if (clearRoute && polylineRef.current) {
      polylineRef.current.setLatLngs([]);
      markerRef.current.setLatLng([20.967, -89.623]);
    }
  }, [clearRoute]);

  const handleStart = () => {
    if (!navigator.geolocation) {
      alert("Tu navegador no soporta geolocalización.");
      return;
    }

    setTripStarted(true); // 👈 Oculta botón iniciar
    setLoading(true);
    setTripData([]);
    setClearRoute(false);
    polylineRef.current.setLatLngs([]);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);

    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLoading(false);
        const { latitude, longitude } = position.coords;
        markerRef.current.setLatLng([latitude, longitude]);
        mapRef.current.panTo([latitude, longitude]);

        watchIdRef.current = navigator.geolocation.watchPosition(
          (pos) => {
            const { latitude, longitude } = pos.coords;
            const ts = new Date(pos.timestamp);
            markerRef.current.setLatLng([latitude, longitude]);
            mapRef.current.panTo([latitude, longitude]);
            polylineRef.current.addLatLng([latitude, longitude]);
            setTripData(prev => [
              ...prev,
              {
                latitude,
                longitude,
                date: ts.toISOString().split("T")[0],
                time: ts.toTimeString().split(" ")[0],
              }
            ]);
          },
          (err) => {
            setLoading(false);
            console.error("Error en seguimiento:", err);
            alert("No se pudo rastrear tu ubicación.");
          },
          { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
        );
      },
      (error) => {
        setLoading(false);
        console.error("Error en getCurrentPosition:", error);
        alert("No se pudo obtener tu ubicación. Revisa los permisos del navegador.");
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  const handleStop = async () => {
    setTripStarted(false); // 👈 Muestra botón iniciar otra vez
    setClearRoute(true);
    if (watchIdRef.current) navigator.geolocation.clearWatch(watchIdRef.current);

    try {
        const res = await fetch("/api/mayatrack", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ data: tripData }),
      });
      if (!res.ok) throw new Error("Error al enviar datos");
      await res.json();
      alert("¡Muchas gracias por aportar a la movilidad de Mérida! 🚶‍♂️");
    } catch (err) {
      console.error("Error al enviar viaje:", err);
      alert("Hubo un error al guardar el viaje 😥");
    }
  };

  return (
    <div style={containerStyle}>
      {showPopup && (
        <MetroMinutoPopup
          onAccept={() => setShowPopup(false)}
          onDecline={() => navigate("/")}
        />
      )}
      <h2 style={titleStyle}>{t("mayatrack.titulo")}</h2>

      <div style={{ position: 'relative' }}>
        <div id="map" style={mapStyle}></div>
        {loading && <div style={overlayStyle}>Cargando ubicación…</div>}
      </div>

      <div style={{ display: 'flex', justifyContent: 'center', gap: '12px', marginBottom: '16px' }}>
        {!tripStarted && (
          <button style={startButtonStyle} onClick={handleStart} disabled={loading}>
            {t("mayatrack.botoniniciar")}
          </button>
        )}
        {tripStarted && (
          <button style={stopButtonStyle} onClick={handleStop}>
            {t("mayatrack.botonfinalizar")}
          </button>
        )}
      </div>

      <button style={backButtonStyle} onClick={() => navigate("/")}>
        {t("mayatrack.botonvolver")}
      </button>
    </div>
  );
};

export default MapComponent;
